import { useState, useEffect , useCallback} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Store, Star, BadgeCheck, Crown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BusinessLogo from "@/components/BusinessLogo";
import TierBadge from "@/components/TierBadge";
import FavoriteButton from "@/components/FavoriteButton";

// Category images featuring Black professionals and businesses
const CATEGORY_IMAGES = {
  "Food & Beverage": "https://images.unsplash.com/photo-1592498542930-4254dc73cbb5?w=400&q=80",
  "Fashion & Apparel": "https://images.unsplash.com/photo-1761370571873-5d869310d731?w=400&q=80",
  "Beauty & Wellness": "https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?w=400&q=80",
  "Home & Living": "https://images.unsplash.com/photo-1622763853951-ded5a33cb724?w=400&q=80",
  "Art & Crafts": "https://images.unsplash.com/photo-1678559612446-2bb33661007c?w=400&q=80",
  "Media and Entertainment": "https://images.unsplash.com/photo-1611414779790-abb3e1ec462e?w=400&q=80",
  "Technology": "https://images.unsplash.com/photo-1689857538296-b6e1a392a91d?w=400&q=80",
  "Professional Services": "https://images.unsplash.com/photo-1621973133111-63649acea55a?w=400&q=80",
  "Health & Fitness": "https://images.unsplash.com/photo-1711006366862-734ee22d1d37?w=400&q=80",
  "Kids & Family": "https://images.pexels.com/photos/5922326/pexels-photo-5922326.jpeg?w=400",
  "Real Estate": "https://images.unsplash.com/photo-1621973133111-63649acea55a?w=400&q=80",
  "Medical Services": "https://images.unsplash.com/photo-1666887360742-974c8fce8e6b?w=400&q=80",
  "Travel": "https://images.pexels.com/photos/6285098/pexels-photo-6285098.jpeg?w=400",
  "Influencer/Content Creator": "https://images.unsplash.com/photo-1613419494224-22b235d11cf3?w=400&q=80",
  "Podcaster": "https://images.unsplash.com/photo-1648522168784-067e98df88c0?w=400&q=80",
  "Streamer": "https://images.pexels.com/photos/7862510/pexels-photo-7862510.jpeg?w=400",
  "Transportation": "https://images.unsplash.com/photo-1696992443043-7d63e521b91c?w=400&q=80",
  "HVAC": "https://images.unsplash.com/photo-1631545308456-c30b8ed398a6?w=400&q=80",
  "Plumbing": "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=400&q=80",
  "Electrical": "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&q=80",
  "Masonry": "https://images.unsplash.com/photo-1590247813693-5541d1c609fd?w=400&q=80",
  "Construction": "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&q=80",
  "Roofing": "https://images.unsplash.com/photo-1632759145355-8b8f96d9f2c1?w=400&q=80",
  "Landscaping": "https://images.unsplash.com/photo-1558904541-efa843a96f01?w=400&q=80",
  "Cleaning Services": "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&q=80",
  "Automotive": "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&q=80",
  "Painting": "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&q=80",
  "Carpentry": "https://images.unsplash.com/photo-1601058268499-e52658b8bb88?w=400&q=80",
  "Handyman": "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=400&q=80",
  "Other": "https://images.unsplash.com/photo-1739302750743-06315cb81f5d?w=400&q=80"
};

// BC Score Stars Component
const BCScoreStars = ({ score, count }) => {
  const fullStars = Math.floor(score);
  const hasHalfStar = score % 1 >= 0.5;
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={`star-${i}`}
            className={`w-3 h-3 ${
              i < fullStars
                ? "fill-tbc-gold text-tbc-gold"
                : i === fullStars && hasHalfStar
                ? "fill-tbc-gold/50 text-tbc-gold"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
      <span className="text-xs text-gray-500 font-body ml-1">
        {score > 0 ? `${score}/5` : "No ratings"} {count > 0 && `(${count})`}
      </span>
    </div>
  );
};

export default function DirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "all");
  const [states, setStates] = useState([]);
  const { user } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  useEffect(() => {
    fetchCategories();
    fetchStates();
    // Mount-once fetch; fetchCategories/fetchStates are stable component-scoped fns.
  }, []);

  useEffect(() => {
    // Hydrate the caller's existing favorites so the hearts render filled
    // for businesses they've already saved. Silently no-op for guests.
    if (!user) {
      setFavoriteIds(new Set());
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/favorites/ids`, { withCredentials: true });
        setFavoriteIds(new Set(res.data.business_ids || []));
      } catch {
        // Non-fatal; hearts just default to empty state.
      }
    })();
  }, [user]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching categories:", error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/businesses/states`);
      setStates(response.data.states || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching states:", error);
    }
  };

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const searchVal = searchParams.get("search");
      const locationVal = searchParams.get("location");
      const categoryVal = searchParams.get("category");
      
      if (searchVal) params.append("search", searchVal);
      if (locationVal) params.append("location", locationVal);
      if (categoryVal && categoryVal !== "all") params.append("category", categoryVal);
      const stateVal = searchParams.get("state");
      if (stateVal && stateVal !== "all") params.append("state", stateVal);

      const response = await axios.get(`${API}/businesses?${params.toString()}`);
      setBusinesses(response.data.businesses || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error fetching businesses:", error);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchBusinesses();
    // fetchBusinesses reads searchParams inside; only searchParams should trigger refetch.
  }, [fetchBusinesses]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (location) params.set("location", location);
    if (category && category !== "all") params.set("category", category);
    if (stateFilter && stateFilter !== "all") params.set("state", stateFilter);
    setSearchParams(params);
  };

  const handleStateChange = (value) => {
    setStateFilter(value);
    const params = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      params.set("state", value);
    } else {
      params.delete("state");
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    const params = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      params.set("category", value);
    } else {
      params.delete("category");
    }
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <section className="bg-tbc-cream grain-texture py-16">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl text-tbc-navy mb-4">
            Business Directory
          </h1>
          <p className="text-gray-600 font-body text-lg max-w-2xl">
            Explore our community of Black-owned businesses
          </p>

          {/* Search & Filter */}
          <form onSubmit={handleSearch} className="mt-8 space-y-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Business Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search businesses or products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 h-12 rounded-lg border-gray-200 focus:border-tbc-navy focus:ring-tbc-navy/20"
                  data-testid="directory-search-input"
                />
              </div>

              {/* Location Search */}
              <div className="lg:w-64 relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="City, State, or ZIP..."
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="pl-12 h-12 rounded-lg border-gray-200 focus:border-tbc-navy focus:ring-tbc-navy/20"
                  data-testid="location-search-input"
                />
              </div>

              {/* Category Filter */}
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="lg:w-[200px] h-12 rounded-lg" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* State Filter — only states that actually have businesses */}
              <Select value={stateFilter} onValueChange={handleStateChange}>
                <SelectTrigger className="lg:w-[180px] h-12 rounded-lg" data-testid="state-filter">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {states.map((s) => (
                    <SelectItem key={s.state} value={s.state}>
                      {s.state} ({s.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                type="submit"
                className="bg-tbc-navy hover:bg-tbc-navy/90 text-white h-12 px-8 rounded-lg"
                data-testid="directory-search-button"
              >
                Search
              </Button>
            </div>

            {/* Active Filters */}
            {(searchParams.get("search") || searchParams.get("location") || (searchParams.get("category") && searchParams.get("category") !== "all")) && (
              <div className="flex flex-wrap gap-2 pt-2">
                {searchParams.get("search") && (
                  <span className="inline-flex items-center px-3 py-1 bg-white rounded-full text-sm font-body">
                    Search: {searchParams.get("search")}
                    <button
                      onClick={() => {
                        setSearch("");
                        const params = new URLSearchParams(searchParams);
                        params.delete("search");
                        setSearchParams(params);
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchParams.get("location") && (
                  <span className="inline-flex items-center px-3 py-1 bg-white rounded-full text-sm font-body">
                    <MapPin className="w-3 h-3 mr-1" />
                    {searchParams.get("location")}
                    <button
                      onClick={() => {
                        setLocation("");
                        const params = new URLSearchParams(searchParams);
                        params.delete("location");
                        setSearchParams(params);
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchParams.get("category") && searchParams.get("category") !== "all" && (
                  <span className="inline-flex items-center px-3 py-1 bg-white rounded-full text-sm font-body">
                    {searchParams.get("category")}
                    <button
                      onClick={() => {
                        setCategory("all");
                        const params = new URLSearchParams(searchParams);
                        params.delete("category");
                        setSearchParams(params);
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Founding Businesses callout — pops up when filtering by state or search.
              Founding members are the pioneers of the community; surface them
              prominently so first-time visitors see them right at the top. */}
          {!loading && businesses.filter((b) => b.is_founding_member).length > 0 && (
            <div
              className="mb-8 rounded-2xl bg-tbc-navy p-6 sm:p-7 border border-tbc-gold/30"
              data-testid="directory-founding-callout"
            >
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-tbc-gold" />
                <h3 className="font-heading text-white text-lg sm:text-xl">
                  Founding Businesses
                  {searchParams.get("state") && searchParams.get("state") !== "all"
                    ? ` in ${searchParams.get("state")}`
                    : ""}
                </h3>
                <span className="text-xs uppercase tracking-widest text-tbc-gold ml-1">
                  · pioneers
                </span>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
                {businesses
                  .filter((b) => b.is_founding_member)
                  .slice(0, 12)
                  .map((b) => (
                    <Link
                      key={`founding-${b.business_id}`}
                      to={`/business/${b.business_id}`}
                      className="flex-shrink-0 w-40 rounded-xl bg-white overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all"
                      data-testid={`founding-card-${b.business_id}`}
                    >
                      <div className="aspect-square bg-tbc-cream/40 flex items-center justify-center p-2">
                        <BusinessLogo
                          name={b.name}
                          logoUrl={b.logo_url}
                          size="xl"
                          shape="square"
                        />
                      </div>
                      <div className="p-2.5">
                        <p className="font-heading text-tbc-navy text-sm truncate">
                          {b.name}
                        </p>
                        <p className="text-[11px] text-gray-500 truncate">
                          {b.city}
                          {b.state ? `, ${b.state}` : ""}
                        </p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-8">
            <p className="text-gray-600 font-body">
              {total} {total === 1 ? "business" : "businesses"} found
              {searchParams.get("location") && ` in "${searchParams.get("location")}"`}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-80 animate-pulse"></div>
              ))}
            </div>
          ) : businesses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business) => (
                <Link
                  key={business.business_id}
                  to={`/business/${business.business_id}`}
                  className={`group bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300 ${
                    business.is_verified
                      ? "border-2 border-tbc-gold/70 shadow-[0_0_0_1px_rgba(212,175,55,0.15)]"
                      : "border border-gray-100"
                  }`}
                  data-testid={`directory-business-${business.business_id}`}
                >
                  <div className="aspect-[4/3] relative overflow-hidden bg-tbc-cream/70 flex items-center justify-center">
                    <BusinessLogo
                      name={business.name}
                      logoUrl={business.logo_url}
                      size="2xl"
                      shape="rounded"
                    />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-tbc-navy rounded-full">
                        {business.category === "Other" && business.custom_category 
                          ? business.custom_category 
                          : business.category}
                      </span>
                      {business.is_verified && (
                        <span className="px-2 py-1 bg-tbc-gold text-tbc-navy text-xs font-medium rounded-full flex items-center gap-1">
                          <BadgeCheck className="w-3 h-3" />
                          Verified
                        </span>
                      )}
                      <TierBadge business={business} size="sm" />
                    </div>
                    <div className="absolute top-4 right-4">
                      <FavoriteButton
                        businessId={business.business_id}
                        initial={favoriteIds.has(business.business_id)}
                        size="sm"
                        stopClickBubble
                        onChange={(fav) => {
                          setFavoriteIds((prev) => {
                            const next = new Set(prev);
                            if (fav) next.add(business.business_id);
                            else next.delete(business.business_id);
                            return next;
                          });
                        }}
                      />
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-heading text-xl text-tbc-navy group-hover:text-tbc-gold transition-colors">
                        {business.name}
                      </h3>
                    </div>
                    
                    {/* BC Score */}
                    <div className="mt-2">
                      <BCScoreStars score={business.bc_score || 0} count={business.bc_score_count || 0} />
                    </div>
                    
                    <p className="text-gray-600 text-sm font-body line-clamp-2 mt-2">
                      {business.description}
                    </p>
                    {business.city && business.state && (
                      <div className="flex items-center text-gray-400 text-xs font-body mt-3">
                        <MapPin className="w-3 h-3 mr-1" />
                        {business.city},{" "}
                        <button
                          type="button"
                          onClick={(e) => {
                            // Prevent the outer <Link> from navigating to the
                            // business detail page — we want to filter instead.
                            e.preventDefault();
                            e.stopPropagation();
                            handleStateChange(business.state);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className="ml-1 text-tbc-navy hover:text-tbc-gold underline decoration-dotted underline-offset-2 transition-colors"
                          title={`Show all businesses in ${business.state}`}
                          data-testid={`state-chip-${business.business_id}`}
                        >
                          {business.state}
                        </button>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-tbc-navy mb-2">No businesses found</h3>
              <p className="text-gray-500 font-body mb-6">
                {search || location || category !== "all"
                  ? "Try adjusting your search or filters"
                  : "Be the first to register your business!"}
              </p>
              <Link to="/dashboard">
                <Button
                  className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-8 py-6"
                  data-testid="register-business-empty"
                >
                  Register Your Business
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
